/**
 * Auth Context
 *
 * Provides centralized authentication state management including:
 * - User authentication and session management
 * - Token lifecycle with automatic refresh
 * - Login/logout/registration flows
 * - Role-based access control helpers
 * - Token persistence in localStorage
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import { authService } from '../services/api';
import type {
  ApiError,
  LoginRequest,
  RegisterRequest,
} from '../services/api/types';
import type { User, AuthState } from '../types/contexts';

/**
 * Auth context value
 */
export interface AuthContextValue extends AuthState {
  // Actions
  login: (credentials: LoginRequest) => Promise<boolean>;
  logout: () => Promise<void>;
  register: (data: RegisterRequest) => Promise<boolean>;
  checkAuthStatus: () => Promise<boolean>;
  clearError: () => void;

  // Computed properties
  hasRole: (role: string) => boolean;
  permissions: string[];
}

/**
 * Storage keys for auth tokens
 */
const ACCESS_TOKEN_KEY = 'quiz_commerce_access_token';
const REFRESH_TOKEN_KEY = 'quiz_commerce_refresh_token';
const USER_KEY = 'quiz_commerce_user';

/**
 * Token refresh interval (15 minutes before expiration)
 */
const TOKEN_REFRESH_INTERVAL = 15 * 60 * 1000; // 15 minutes

/**
 * Type guard for ApiError
 */
function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error
  );
}

/**
 * Convert unknown error to ApiError
 */
function toApiError(error: unknown): ApiError {
  if (isApiError(error)) {
    return error;
  }

  if (error instanceof Error) {
    return {
      code: 'UNKNOWN_ERROR',
      message: error.message,
    };
  }

  return {
    code: 'UNKNOWN_ERROR',
    message: 'An unexpected error occurred',
  };
}

/**
 * Parse JWT token to extract user info
 */
function parseJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Check if token is expired
 */
function isTokenExpired(token: string): boolean {
  const payload = parseJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') {
    // If we can't parse the token or it doesn't have an expiration,
    // assume it's still valid (server will validate it)
    return false;
  }

  // Check if token expires in next 5 minutes
  const expirationTime = payload.exp * 1000;
  const bufferTime = 5 * 60 * 1000; // 5 minutes
  return Date.now() >= expirationTime - bufferTime;
}

/**
 * Auth Context
 * Exported for use by the useAuth hook
 */
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Auth Provider Props
 */
export interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Auth Provider Component
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  /**
   * Persist tokens to localStorage
   */
  const persistTokens = useCallback((access: string, refresh: string) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, access);
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  }, []);

  /**
   * Persist user to localStorage
   */
  const persistUser = useCallback((userData: User) => {
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
  }, []);

  /**
   * Clear all auth data
   */
  const clearAuthData = useCallback(() => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
  }, []);

  /**
   * Refresh access token
   */
  const refreshAccessToken = useCallback(async (): Promise<boolean> => {
    const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!storedRefreshToken) {
      return false;
    }

    try {
      const response = await authService.refreshToken({
        refreshToken: storedRefreshToken,
      });

      if (response.success && response.data) {
        const newAccessToken = response.data.accessToken;
        localStorage.setItem(ACCESS_TOKEN_KEY, newAccessToken);
        setAccessToken(newAccessToken);
        return true;
      }

      // Refresh failed - clear auth data
      clearAuthData();
      return false;
    } catch (err) {
      clearAuthData();
      return false;
    }
  }, [clearAuthData]);

  /**
   * Check authentication status and restore session
   */
  const checkAuthStatus = useCallback(async (): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const storedAccessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      const storedUserData = localStorage.getItem(USER_KEY);

      if (!storedAccessToken || !storedRefreshToken || !storedUserData) {
        setLoading(false);
        return false;
      }

      // Check if token is expired
      if (isTokenExpired(storedAccessToken)) {
        // Try to refresh
        const refreshed = await refreshAccessToken();
        if (!refreshed) {
          setLoading(false);
          return false;
        }
      } else {
        setAccessToken(storedAccessToken);
      }

      // Restore user data
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- JSON.parse returns any, validated by User interface
      const userData: User = JSON.parse(storedUserData);
      setUser(userData);
      setRefreshToken(storedRefreshToken);
      setLoading(false);
      return true;
    } catch (err) {
      clearAuthData();
      setLoading(false);
      return false;
    }
  }, [refreshAccessToken, clearAuthData]);

  /**
   * Login user
   */
  const login = useCallback(
    async (credentials: LoginRequest): Promise<boolean> => {
      try {
        setLoading(true);
        setError(null);

        const response = await authService.login(credentials);

        if (!response.success || !response.data) {
          const apiError = response.error || {
            code: 'LOGIN_FAILED',
            message: 'Login failed',
          };
          setError(apiError);
          setLoading(false);
          return false;
        }

        const { accessToken: access, refreshToken: refresh, user: userData } = response.data;

        // Persist tokens and user data
        persistTokens(access, refresh);
        persistUser(userData);

        // Update state
        setAccessToken(access);
        setRefreshToken(refresh);
        setUser(userData);
        setLoading(false);

        return true;
      } catch (err) {
        const apiError = toApiError(err);
        setError(apiError);
        setLoading(false);
        return false;
      }
    },
    [persistTokens, persistUser]
  );

  /**
   * Logout user
   */
  const logout = useCallback(async (): Promise<void> => {
    try {
      // Call logout endpoint to invalidate token on server
      await authService.logout();
    } catch (err) {
      // Continue with logout even if server call fails
      console.error('Logout error:', err);
    } finally {
      clearAuthData();
    }
  }, [clearAuthData]);

  /**
   * Register new user
   */
  const register = useCallback(
    async (data: RegisterRequest): Promise<boolean> => {
      try {
        setLoading(true);
        setError(null);

        const response = await authService.register(data);

        if (!response.success || !response.data) {
          const apiError = response.error || {
            code: 'REGISTRATION_FAILED',
            message: 'Registration failed',
          };
          setError(apiError);
          setLoading(false);
          return false;
        }

        // For registration, we get accessToken but need to construct user object
        const { accessToken: access, userId } = response.data;

        // Store access token
        localStorage.setItem(ACCESS_TOKEN_KEY, access);
        setAccessToken(access);

        // Create user object from registration data
        const userData: User = {
          id: userId,
          email: data.email,
          tenantId: data.tenantId,
          roles: ['user'], // Default role
          profile: data.profile,
        };

        persistUser(userData);
        setUser(userData);
        setLoading(false);

        return true;
      } catch (err) {
        const apiError = toApiError(err);
        setError(apiError);
        setLoading(false);
        return false;
      }
    },
    [persistUser]
  );

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Check if user has specific role
   */
  const hasRole = useCallback(
    (role: string): boolean => {
      return user?.roles.includes(role) ?? false;
    },
    [user]
  );

  /**
   * Get user permissions (derived from roles)
   */
  const permissions = useMemo(() => {
    if (!user) return [];

    // In a real app, this would map roles to permissions
    // For now, return roles as permissions
    return user.roles;
  }, [user]);

  /**
   * Computed isAuthenticated flag
   */
  const isAuthenticated = useMemo(() => {
    return !!(user && accessToken);
  }, [user, accessToken]);

  /**
   * Set up automatic token refresh
   */
  useEffect(() => {
    if (!isAuthenticated || !refreshToken) {
      return;
    }

    const refreshInterval = setInterval(() => {
      void refreshAccessToken();
    }, TOKEN_REFRESH_INTERVAL);

    return () => {
      clearInterval(refreshInterval);
    };
  }, [isAuthenticated, refreshToken, refreshAccessToken]);

  /**
   * Check auth status on mount
   */
  useEffect(() => {
    void checkAuthStatus();
  }, [checkAuthStatus]);

  /**
   * Context value
   */
  const contextValue: AuthContextValue = {
    user,
    accessToken,
    refreshToken,
    isAuthenticated,
    loading,
    error,
    login,
    logout,
    register,
    checkAuthStatus,
    clearError,
    hasRole,
    permissions,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook to use auth context
 */
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
};
